import React, { useMemo } from 'react';
import { useAuthNavigation } from '../../shared/navigation/useAuthNavigation';
import { View, StyleSheet, useWindowDimensions } from 'react-native';

import type { ThemeColors } from '../../shared/theme';
import { androidFlatSurfaceStyle } from '../../shared/theme/sharedStyles';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { SvgIcon } from '../../components/common/SvgIcon';
import { useTranslation } from '../../shared/i18n';
import { studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import { useDashboardVariant } from '../../data/domains/config';
import { DashboardQuickActionsSet1 } from './variants/set1/DashboardQuickActionsSet1';

interface ActionItem {
  key: string;
  icon: string;
  labelKey: string;
  route: string;
  params?: object;
}

const ALL_ACTIONS: ActionItem[] = [
  { key: 'journey', icon: 'journey', labelKey: 'dashboard.journey', route: 'Journey', params: { from: 'dashboard' } },
  { key: 'subscriptions', icon: 'card', labelKey: 'dashboard.subscriptions', route: 'MySubscriptions' },
  { key: 'notifications', icon: 'bell', labelKey: 'dashboard.notifications', route: 'Notifications' },
  { key: 'consultations', icon: 'users', labelKey: 'dashboard.consultations', route: 'Consultation' },
  { key: 'loyalty', icon: 'trophy', labelKey: 'dashboard.rewards', route: 'Loyalty' },
  { key: 'addresses', icon: 'map', labelKey: 'dashboard.addresses', route: 'AddressLists' },
  { key: 'news', icon: 'news', labelKey: 'dashboard.news', route: 'News' },
  { key: 'community', icon: 'chat', labelKey: 'community.title', route: 'Community' },
  { key: 'settings', icon: 'settings', labelKey: 'dashboard.settings', route: 'Settings' },
];

const REF_WIDTH = 390;
const ACTIONS_PER_ROW = 4;

type QuickActionMetrics = ReturnType<typeof buildQuickActionMetrics>;

function buildQuickActionMetrics(window_width: number, font_scale: number) {
  const ratio = window_width / REF_WIDTH;
  const scale = Math.min(1.06, Math.max(0.86, ratio)) / Math.max(font_scale, 1);
  const ss = (size: number) => Math.round(size * scale);

  return {
    tile_radius: ss(18),
    icon_size: ss(22),
    row_gap: ss(10),
    col_gap: ss(10),
  };
}

interface DashboardQuickActionsProps {
  colors: ThemeColors;
  show_community_action: boolean;
  show_consultations_action: boolean;
  show_loyalty_action: boolean;
  show_journey_action: boolean;
}

function buildActionRows(
  show_community_action: boolean,
  show_consultations_action: boolean,
  show_loyalty_action: boolean,
  show_journey_action: boolean,
): ActionItem[][] {
  const actions = ALL_ACTIONS.filter(a => {
    if (a.key === 'journey' && !show_journey_action) {
      return false;
    }
    if (a.key === 'consultations' && !show_consultations_action) {
      return false;
    }
    if (a.key === 'loyalty' && !show_loyalty_action) {
      return false;
    }
    return (
      (a.key !== 'community' && a.key !== 'settings') ||
      (show_community_action && a.key === 'community') ||
      (!show_community_action && a.key === 'settings')
    );
  });
  return [actions.slice(0, ACTIONS_PER_ROW), actions.slice(ACTIONS_PER_ROW, ACTIONS_PER_ROW * 2)];
}

function createQuickActionStyles(m: QuickActionMetrics) {
  return StyleSheet.create({
    grid: {
      gap: m.row_gap,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'stretch',
      columnGap: m.col_gap,
    },
    card: {
      flex: 1,
      minWidth: 0,
    },
    cardClickContent: {
      width: '100%',
    },
    cardButton: {
      width: '100%',
      aspectRatio: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
}

function DashboardQuickActionsLegacy({
  colors,
  show_community_action,
  show_consultations_action,
  show_loyalty_action,
  show_journey_action,
}: DashboardQuickActionsProps) {
  const navigation = useAuthNavigation();
  const { t } = useTranslation();
  const { width: window_width, fontScale: font_scale } = useWindowDimensions();
  const metrics = useMemo(() => buildQuickActionMetrics(window_width, font_scale), [window_width, font_scale]);
  const styles = useMemo(() => createQuickActionStyles(metrics), [metrics]);
  const action_rows = useMemo(
    () => buildActionRows(show_community_action, show_consultations_action, show_loyalty_action, show_journey_action),
    [show_community_action, show_consultations_action, show_loyalty_action, show_journey_action],
  );
  const tile_shell = useMemo(() => studioCardShell(colors), [colors]);

  return (
    <View style={styles.grid}>
      {action_rows.map((row_items, row_index) => (
        <View key={row_index} style={styles.row}>
          {row_items.map(action => {
            const label = t(action.labelKey);
            return (
              <View
                key={action.key}
                style={[
                  styles.card,
                  tile_shell,
                  androidFlatSurfaceStyle(),
                  {
                    backgroundColor: colors.surface,
                    borderRadius: metrics.tile_radius,
                  },
                ]}>
                <Clickable
                  onPress={() => navigation.navigate(action.route, action.params)}
                  accessibilityLabel={label}
                  accessibilityRole="button"
                  pressScale={PRESS_PRESETS.listRow.scale}
                  haptics
                  useNativePressable
                  contentStyle={styles.cardClickContent}>
                  <View style={styles.cardButton}>
                    <SvgIcon name={action.icon as any} size={metrics.icon_size} color={colors.primary} />
                  </View>
                </Clickable>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
}

export function DashboardQuickActions(props: DashboardQuickActionsProps) {
  const variant = useDashboardVariant();

  // SET1 vertical pill + elevated surfaces; SET2/SET3 share the same RTL/surface pattern.
  if (variant === 'set1' || variant === 'set2' || variant === 'set3') {
    return <DashboardQuickActionsSet1 {...props} />;
  }

  return <DashboardQuickActionsLegacy {...props} />;
}
